import { from, map, merge, of, switchMap } from 'rxjs';
import { format as formatDateTime } from '../../../../../../utils/formatting';
import type { GraphTimeWindow } from '../../../elements/types';
import type { RuntimeDataServiceInput, RuntimeDataServiceOutput } from '../contracts';
import { RuntimeDataService } from '../types';
import type {
    RuntimeDataServiceRequest,
    RuntimeResultLinkDescriptor,
    RuntimeReactiveBindingInputArgs,
} from '../types';
import {
    isRecord,
    normalizeNonEmptyString,
    normalizeRequiredFiniteNumber,
} from '../validationPrimitives';

function normalizeGraphTimeWindow(value: unknown): GraphTimeWindow {
    if (!isRecord(value)) {
        throw new Error('Expected "report.graphTimeWindow" to be an object.');
    }

    return {
        viewStartTs: normalizeRequiredFiniteNumber(
            value.viewStartTs,
            'report.graphTimeWindow.viewStartTs'
        ),
        viewEndTs: normalizeRequiredFiniteNumber(
            value.viewEndTs,
            'report.graphTimeWindow.viewEndTs'
        ),
    };
}

function validateReportContextInput(input: unknown): RuntimeDataServiceInput<'report'> {
    if (!isRecord(input)) {
        throw new Error('Expected "report" input to be an object.');
    }

    return {
        timezone: normalizeNonEmptyString(input.timezone, 'report.timezone'),
        graphTimeWindow: normalizeGraphTimeWindow(input.graphTimeWindow),
    };
}

function validateReportContextResult(result: unknown): RuntimeDataServiceOutput<'report'> {
    if (!isRecord(result)) {
        throw new Error('Expected "report" result to be an object.');
    }

    return {
        period_start: normalizeNonEmptyString(result.period_start, 'report.period_start'),
        period_end: normalizeNonEmptyString(result.period_end, 'report.period_end'),
        period: normalizeNonEmptyString(result.period, 'report.period'),
    };
}

export function formatReportPeriod(timezone: string, graphTimeWindow: GraphTimeWindow) {
    const start = formatDateTime(new Date(graphTimeWindow.viewStartTs), {
        timezone,
        hideSeconds: true,
    });
    const end = formatDateTime(new Date(graphTimeWindow.viewEndTs), {
        timezone,
        hideSeconds: true,
    });

    return {
        period_start: start,
        period_end: end,
        period: `${start} - ${end}`,
    };
}

function resolveReportPeriodInput$({
    context,
    graphTimeWindow,
    getDataSetContext,
}: RuntimeReactiveBindingInputArgs) {
    if (context?.kind !== 'project' && context?.kind !== 'measuring-point') {
        throw new Error(
            'Report period binding requires project or measuring-point render context.'
        );
    }

    if (!graphTimeWindow) {
        throw new Error('Report period binding requires graph time window.');
    }

    if (context.kind === 'project') {
        return of({
            timezone: normalizeNonEmptyString(context.project.timezone, 'report.timezone'),
            graphTimeWindow,
        });
    }

    const { dataSet, dataSetRequestError$ } = getDataSetContext();
    return merge(
        // Wait for loaded records/config before accepting the initial UTC value.
        from(dataSet.ready).pipe(switchMap(() => dataSet.timezone$)),
        dataSetRequestError$.pipe(
            map((error): never => {
                throw error;
            })
        )
    ).pipe(map((timezone: string) => ({ timezone, graphTimeWindow })));
}

function createReportPeriodLink(
    key: 'period_start' | 'period_end' | 'period',
    label: string,
    description: string
): RuntimeResultLinkDescriptor<'report'> & {
    resolveBuildInput$: typeof resolveReportPeriodInput$;
} {
    return {
        key,
        label,
        description,
        supportedContextKinds: ['project', 'measuring-point'],
        supportedSwarmTypes: null,
        path: key,
        valueKind: 'scalar',
        argPolicy: 'forbidden',
        exposeInInsertionPresets: true,
        requiresGraphTimeWindow: true,
        resolveBuildInput$: resolveReportPeriodInput$,
    };
}

export class ReportRuntimeDataService extends RuntimeDataService<'report'> {
    static readonly serviceId = 'report' as const;

    static get serviceMetadata() {
        return {
            label: gettext('DATA_SERVICE_REPORT_LABEL'),
            description: gettext('DATA_SERVICE_REPORT_DESCRIPTION'),
        } as const;
    }

    static get serviceResultLinks() {
        return [
            createReportPeriodLink(
                'period_start',
                gettext('DATA_SERVICE_REPORT_PERIOD_START_LABEL'),
                gettext('DATA_SERVICE_REPORT_PERIOD_START_DESCRIPTION')
            ),
            createReportPeriodLink(
                'period_end',
                gettext('DATA_SERVICE_REPORT_PERIOD_END_LABEL'),
                gettext('DATA_SERVICE_REPORT_PERIOD_END_DESCRIPTION')
            ),
            createReportPeriodLink(
                'period',
                gettext('DATA_SERVICE_REPORT_PERIOD_LABEL'),
                gettext('DATA_SERVICE_REPORT_PERIOD_DESCRIPTION')
            ),
        ] as const satisfies readonly RuntimeResultLinkDescriptor<'report'>[];
    }

    validateArgs = validateReportContextInput;

    validateResult = validateReportContextResult;

    createResult$ = ({ input }: RuntimeDataServiceRequest<'report'>) =>
        of(formatReportPeriod(input.timezone, input.graphTimeWindow));
}

export function createReportService(): ReportRuntimeDataService {
    return new ReportRuntimeDataService();
}
